import { useEffect, useRef } from 'react';

export function usePanelBehavior(
  open: boolean,
  onOpenChange: (open: boolean) => void,
  enableClickOutside: boolean = true,
  excludeSelectors?: string[],
  onClose?: () => void // callback to execute when panel closes
) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (onClose) {
          onClose();
        }
        onOpenChange(false);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (enableClickOutside) {
        window.requestAnimationFrame(() => {
          const target = event.target as Node;

          // Check if click is inside the panel
          if (panelRef.current && panelRef.current.contains(target)) {
            return;
          }

          // Check if click is on an excluded element (e.g., table rows)
          if (excludeSelectors && excludeSelectors.length > 0) {
            const element = event.target as Element;
            for (const selector of excludeSelectors) {
              if (element.closest(selector)) {
                return; // Don't close if clicking on excluded element
              }
            }
          }

          // Execute close callback before closing
          if (onClose) {
            onClose();
          }
          // Close the panel
          onOpenChange(false);
        });
      }
    };

    if (open) {
      if (enableClickOutside) {
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleClickOutside);
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onOpenChange, enableClickOutside, excludeSelectors, onClose]);

  return { panelRef };
}
